import * as React from 'react';

import { compose } from 'recompose';

import createLoadableComponent from 'js/lib/createLoadableComponent';
import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import AutoenrolledCoursesModal from 'bundles/enterprise-legacy-learner-home/components/modals/AutoenrolledCoursesModal';
import ForcedSSORecoveryEmailModal from 'bundles/enterprise-legacy-learner-home/components/modals/ForcedSSORecoveryEmailModal';
import ProgramAccessModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramAccessModal';
import ProgramAddEmailModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramAddEmailModal';
import ProgramAlternativeEmailModal from 'bundles/enterprise-legacy-learner-home/components/modals/ProgramAlternativeEmailModal';
import ProgramLearnerApiManager from 'bundles/enterprise-legacy-learner-home/utils/ProgramLearnerApiManager';
import type { InjectedNaptime } from 'bundles/naptimejs';
import Naptime from 'bundles/naptimejs';
import type EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import type ThirdPartyOrganizationsV1Type from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import useHasThirdPartyOrgEnforcedSSOLogin from 'bundles/saml-sso/shared/hooks/useHasThirdPartyOrgEnforcedSSOLogin';
import { useEnterpriseFeatureFlags } from 'bundles/saml-sso/shared/hooks/useProductFeatureFlags';
import { ENTERPRISE_MODAL_NAMES } from 'bundles/unified-common/constants/enterprise';
import { useEnterpriseAutoEnrolledCourses } from 'bundles/unified-common/contexts/EnterpriseAutoEnrolledCoursesContext';
import { useEnterpriseModalManager } from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useIsGwGProgram,
  useProgram,
  useProgramMembership,
  useUserEmails,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import type { ProgramJoinErrorModalInfo } from 'bundles/unified-common/types/enterprise';
import ProgramAtCapacityModal from 'bundles/unified-description-page-common/components/shared/modals/ProgramAtCapacityModal';

const EnterpriseTermsOfUseModal = createLoadableComponent(
  () => import('bundles/unified-common/components/enterprise/EnterpriseTermsOfUseModal')
);

type Props = {
  naptime: InjectedNaptime;
};

const EnterpriseModalManager = ({ naptime }: Props) => {
  const router = useRouter();
  const modalManager = useEnterpriseModalManager();
  const program = useProgram();
  const programMembership = useProgramMembership();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const isGwGProgram = useIsGwGProgram();
  const { userEmails } = useUserEmails();
  const autoEnrolledCourses = useEnterpriseAutoEnrolledCourses();
  const features = useEnterpriseProductFeatures();
  const { email_address: emailAddress } = user.get();
  const { isSsoLoginForced } = useHasThirdPartyOrgEnforcedSSOLogin(thirdPartyOrganization?.id || '', emailAddress);
  const { isUnifiedSsoEnabled: isUnifiedSso } = useEnterpriseFeatureFlags(thirdPartyOrganization?.id || '');

  const setProgramJoinErrorModalState = React.useCallback<
    (programJoinErrorModalInfo: ProgramJoinErrorModalInfo) => void
  >(
    (programJoinModalErrorInfo) => {
      modalManager?.setProgramJoinErrorInfo(programJoinModalErrorInfo);
    },
    [modalManager]
  );

  if (!program || !thirdPartyOrganization) {
    return null;
  }

  const apiManager = new ProgramLearnerApiManager({ program, naptime });
  if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.ENTERPRISE_TERMS_OF_USE) {
    return (
      <EnterpriseTermsOfUseModal
        showTermsOfUseModal={modalManager.showEnterpriseTermsOfUseModal}
        setShowTermsOfUseModal={modalManager.setShowEnterpriseTermsOfUseModal}
        thirdPartyOrganization={thirdPartyOrganization}
        isEnterpriseAdmin={modalManager.isEnterpriseAdmin}
        shouldShowCCPAMarketingConsent={modalManager.shouldShowCCPAMarketingConsent}
        shouldShowGDPRMarketingConsent={modalManager.shouldShowGDPRMarketingConsent}
        shouldShowThirdPartyTermsOfConsent={modalManager.shouldShowThirdPartyTermsOfConsent}
      />
    );
  }
  if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.PROGRAM_ACCESS) {
    return (
      <ProgramAccessModal
        apiManager={apiManager}
        isAccessModalDismissed={modalManager?.isAccessModalDismissed}
        isProgramAdmin={!!programMembership.isProgramAdmin}
        onCloseAccessModal={() => modalManager?.setIsAccessModalDismissed?.(true)}
        openProgramJoinErrorModal={setProgramJoinErrorModalState}
        program={
          {
            ...program,
            name: program?.metadata?.name,
            slug: program?.metadata?.slug,
          } as EnterpriseProgramsV1
        }
        programMembership={programMembership?.membership}
        reloadProgramMembership={() => {}}
        router={router}
        thirdPartyOrganization={thirdPartyOrganization as ThirdPartyOrganizationsV1Type}
        userId={user.get()?.id}
        // Not used in component.
        isUpswell={false}
        // Not used in component.
        isNonBlacklistedC4cv={false}
        isGwGProgram={isGwGProgram}
        programRecommendationsAvailableCoursesCopy={!!features?.programRecommendationsAvailableCoursesCopy}
        autoEnrolledCoursesWithData={autoEnrolledCourses}
      />
    );
  } else if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.LOGIN) {
    router.push({
      pathname: router.location.pathname,
      params: router.params,
      query: { ...router.location.query, authMode: 'login' },
    });

    modalManager.setHasShownLoginModal(true);

    return null;
  } else if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.AUTOENROLLED_COURSES && autoEnrolledCourses) {
    return (
      <AutoenrolledCoursesModal
        userId={user.get()?.id}
        autoEnrolledCoursesWithData={autoEnrolledCourses}
        setIsAutoenrolledCoursesModalDismissed={modalManager?.setIsAutoenrolledCoursesModalDismissed || (() => {})}
        programId={program.id}
        programSlug={program.metadata.slug}
      />
    );
  } else if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.ADD_EMAIL) {
    if (isSsoLoginForced || isUnifiedSso) {
      return <ForcedSSORecoveryEmailModal programId={program.id} userId={user.get()?.id} />;
    }
    return (
      <>
        <ProgramAddEmailModal
          apiManager={apiManager}
          programId={program.id}
          programName={program.metadata.name}
          userId={user.get()?.id}
          thirdPartyOrganizationId={thirdPartyOrganization.id}
          thirdPartyOrganizationSlug={thirdPartyOrganization.slug}
        />
        {userEmails && (
          <ProgramAlternativeEmailModal
            apiManager={apiManager}
            programId={program.id}
            programName={program.metadata.name}
            userEmails={userEmails}
            userId={user.get()?.id}
            thirdPartyOrganizationId={thirdPartyOrganization.id}
            thirdPartyOrganizationSlug={thirdPartyOrganization.slug}
          />
        )}
      </>
    );
  } else if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.PROGRAM_JOIN_ERROR) {
    return (
      <ProgramAtCapacityModal
        programJoinErrorCode={modalManager.programJoinErrorInfo.errorCode!}
        isOpen
        setOpen={(isOpen) => {
          if (!isOpen) {
            setProgramJoinErrorModalState({ errorCode: modalManager.programJoinErrorInfo.errorCode, showModal: false });
          }
        }}
      />
    );
  } else {
    return null;
  }
};

const EnterpriseModalManagerWithErrorBoundary = ({ naptime }: Props) => {
  return (
    <ErrorBoundaryWithTagging componentName="EnterpriseModalManager">
      <EnterpriseModalManager naptime={naptime} />
    </ErrorBoundaryWithTagging>
  );
};

export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterpriseModalManagerWithErrorBoundary);
